//-------------------------------------------------------------------------------------------------- Imports
import { Card } from "antd";
import { StyleSheet } from "../../../StyleSheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { CSSProperties, useEffect, useState } from "react";
import Loading from "../../../components/Loading";
import moment from "moment";
import { ResponsePaymentGeneralDatos } from "../../../types/common";

//-------------------------------------------------------------------------------------------------- Interfaces
interface CardStatusPaymentProps {
    hasRecurrentPayments: boolean;
    isLoading: boolean;                  // 
    currentBalance?: ResponsePaymentGeneralDatos;             // Cantidad actual a pagar.
    hasDebt?: boolean;                   // Indica si tiene un saldo vencido.
    limitPaymentDate?: string;           // Fecha límite de pago.
    actualizationDateTime: Date;         // Fecha de última actualización.
    onrefreshPress: Function;           // Función de cuando se presiona el botón de refresh.
};

//-------------------------------------------------------------------------------------------------- Component
const CardStatusPayment = (props: CardStatusPaymentProps) => {
    const {
        isLoading, currentBalance, hasDebt, limitPaymentDate, actualizationDateTime, onrefreshPress, hasRecurrentPayments
    } = props;
    const withAllData = currentBalance && limitPaymentDate;
    const colorStyle: CSSProperties = (
        hasDebt && withAllData
    ) ? styles.withOverdueColor : styles.withoutOverdueColor;

    const [fullDateFormat, setFullDateFormat] = useState("");
    const [shortDateFormat, setShortDateFormat] = useState("");

    useEffect(() => {
        if (limitPaymentDate) {
            let fullDateFormat = "";
            let shortDateFormat = "";

            const [diaStr, mesStr, yearStr] = limitPaymentDate.split("-");

            const mesMap: Record<string, string> = {
                Ene: "Enero",
                Feb: "Febrero",
                Mar: "Marzo",
                Abr: "Abril",
                May: "Mayo",
                Jun: "Junio",
                Jul: "Julio",
                Ago: "Agosto",
                Sep: "Septiembre",
                Oct: "Octubre",
                Nov: "Noviembre",
                Dic: "Diciembre",
            };

            const mesNumMap: Record<string, string> = {
                Ene: "01",
                Feb: "02",
                Mar: "03",
                Abr: "04",
                May: "05",
                Jun: "06",
                Jul: "07",
                Ago: "08",
                Sep: "09",
                Oct: "10",
                Nov: "11",
                Dic: "12",
            };

            const monthLong = mesMap[mesStr];
            const monthShort = mesNumMap[mesStr];

            const day = parseInt(diaStr, 10);
            const year = parseInt(yearStr, 10);

            fullDateFormat = `${day} de ${monthLong}`;
            shortDateFormat = `${monthShort}-${year}`;
            setFullDateFormat(fullDateFormat);
            setShortDateFormat(shortDateFormat);
        }
    }, [limitPaymentDate]);



    return (
        <div style={styles.mainContainer}>
            <Card style={styles.card}>
                {(isLoading) ? (
                    <Loading />
                ) : (
                    <>
                        <div style={styles.infoContainer}>
                            {(withAllData) ? (
                                <>
                                    <div style={styles.actualizationContainer}>
                                        <p style={styles.pSaldo}>Saldo</p>
                                        <p style={styles.pUpdate}>Última actualización:</p>
                                        <p style={styles.pUpdate}>{moment(actualizationDateTime).format("D [de] MMM yy h:mm")}</p>
                                    </div>
                                    <div style={styles.ammountContainer}>
                                        <p style={{ ...styles.balance, ...colorStyle }}>
                                            ${currentBalance.amount}
                                        </p>
                                    </div>
                                </>

                            ) : (
                                <div style={styles.withoutInformation}>
                                    <p style={colorStyle}>No fue posible realizar la consulta de saldo.</p>
                                </div>
                            )}
                            <div style={styles.refreshContainer} onClick={() => onrefreshPress()}>
                                <FontAwesomeIcon icon={faRotate} style={{ ...styles.iconStyle, ...colorStyle }} />
                            </div>
                        </div>
                        {
                            withAllData && currentBalance.amountQty > 0  && (
                                <div style={styles.periodContainer}>
                                    <p style={(hasDebt) ? colorStyle : styles.regularText}>Periodo que cubre: <b>{shortDateFormat}</b></p>
                                </div>
                            )
                        }

                        {(withAllData && hasDebt) && (
                            <div style={styles.overdueContainer}>
                                <p style={{ ...styles.pOverdue, ...colorStyle }}>Tienes un saldo vencido</p>
                            </div>
                        )}
                        {
                            hasRecurrentPayments ?
                                <p style={styles.recurrentPayments}>Actualmente tu pago está domiciliado</p>
                                : null
                        }

                    </>
                )}
            </Card>
            {(!isLoading && withAllData) && (
                <div style={styles.footerCard}>
                    <p style={(hasDebt) ? colorStyle : styles.regularText}>Tu fecha de corte es el día <b>{fullDateFormat}</b></p>
                </div>
            )}
        </div>
    );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    mainContainer: {
        display: "flex",
        width: "100%",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: 40,
    },
    card: {
        width: "100%",
        borderRadius: 20,
        boxShadow: "0px 0px 8px #00000029",
        padding: "0px 10px",
        lineHeight: 1.4
    },
    infoContainer: {
        display: "flex",
        width: "100%",
        flexDirection: "row",
    },
    withoutInformation: {
        flex: 1,
        display: "flex",
        height: "100%",
    },
    actualizationContainer: {
        display: "flex",
        width: 125,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",

    },
    ammountContainer: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        paddingRight: 10
    },
    refreshContainer: {
        display: "flex",
        width: 75,
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
    },
    iconStyle: {
        fontSize: 36,
        transform: "rotate(90deg)",
    },
    overdueContainer: {
        display: "flex",
        width: "100%",
        justifyContent: "center",
    },
    periodContainer: {
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        paddingRight: 24
    },
    withoutOverdueColor: {
        color: "#0055c2",
        margin: 5
    },
    recurrentPayments: {
        color: "#0055c2",
        margin: 0,
        fontWeight: 600,
        justifyContent: 'center',
        display: 'flex'
    },
    withOverdueColor: {
        color: "#e00614",
        margin: 5
    },
    pSaldo: {
        margin: 0,
        fontSize: 30,
        fontWeight: 600,
    },
    pUpdate: {
        margin: 0,
        fontSize: 10,
    },
    balance: {
        margin: 0,
        fontSize: 35,
        fontWeight: 600
    },
    pOverdue: {
        margin: 0,
        fontWeight: 600
    },
    footerCard: {
        display: "flex",
        width: "90%",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "0px 0px 20px 20px",
        backgroundColor: "#d9d9d9",
        flexDirection: 'column'
    },
    regularText: {
        margin: 5
    }
});

//-------------------------------------------------------------------------------------------------- Exports
export default CardStatusPayment;
